import { random, neighbors, reject } from './53-game-primitives.mjs';

import { createGemGame, gemSwap } from './60-gem-powers.mjs';
export { gemMatches, gemMoves, makeGemBoard } from './60-gem-powers.mjs';

export const CLUSTER_COLUMNS = 16;
export const CLUSTER_CELL_COUNT = CLUSTER_COLUMNS * CLUSTER_COLUMNS;

export const MERGE_COLUMNS = 6;
export const MERGE_CELL_COUNT = MERGE_COLUMNS * MERGE_COLUMNS;
export const MERGE_TARGET = 8192;
/** Canonical earned tile thresholds; presentation may preview the next threshold. */
export const MERGE_MILESTONES = Object.freeze([
  32,
  64,
  128,
  256,
  512,
  1024,
  2048,
  4096,
  MERGE_TARGET,
]);

/** Arcade refill progression makes 8192 reachable inside the shared 600-action
 * bound. Given the canonical largest tile, return the ordinary spawn value;
 * seeded rarity may double it. This never awards points or changes existing tiles. */
export function mergeSpawnBase(largest) {
  return largest >= 4096 ? 64 : largest >= 1024 ? 16 : largest >= 256 ? 8 : largest >= 64 ? 4 : 2;
}

export const BLOCK_SHAPES = [
  [[0, 0]],
  [
    [0, 0],
    [1, 0],
  ],
  [
    [0, 0],
    [1, 0],
    [2, 0],
  ],
  [
    [0, 0],
    [0, 1],
    [1, 1],
  ],
  [
    [0, 0],
    [1, 0],
    [0, 1],
    [1, 1],
  ],
  [
    [0, 0],
    [1, 0],
    [2, 0],
    [1, 1],
  ],
  [
    [0, 0],
    [0, 1],
    [0, 2],
  ],
  [
    [0, 0],
    [1, 0],
    [2, 0],
    [0, 1],
    [1, 1],
  ],
  [
    [0, 0],
    [1, 0],
    [2, 0],
    [1, 1],
    [1, 2],
  ],
];

const levelForTurns = (turns, first, second) => (turns >= second ? 3 : turns >= first ? 2 : 1);
function advanceBoardLevel(state, first, second) {
  const d = state.data,
    level = levelForTurns(d.turns, first, second);
  if (level <= d.level) return;
  d.level = level;
  const points = (level - 1) * 75;
  state.score += points;
  d.events.push({ type: 'level', level, points });
}

export function clusterGroup(board, index) {
  const group = new Set([index]);
  const queue = [index];
  for (let head = 0; head < queue.length; head++)
    for (const next of neighbors(queue[head], CLUSTER_COLUMNS, CLUSTER_CELL_COUNT))
      if (board[next] === board[index] && !group.has(next)) {
        group.add(next);
        queue.push(next);
      }
  return [...group];
}

/** Enumerate each legal connected burst once in stable row-major order on a
 * canonical 16×16 board. This advisory search reads only visible colours and
 * never consumes replay randomness, repairs the board or awards points. */
export function clusterGroups(board) {
  const seen = new Set(),
    groups = [];
  for (let index = 0; index < board.length; index++) {
    if (seen.has(index)) continue;
    const cells = clusterGroup(board, index);
    cells.forEach((cell) => seen.add(cell));
    if (cells.length >= 2) groups.push(cells);
  }
  return groups;
}

export function clusterMoves(board) {
  return board.some((v, i) =>
    neighbors(i, CLUSTER_COLUMNS, CLUSTER_CELL_COUNT).some((j) => board[j] === v),
  );
}

function clusterBoard(state, colourCount = 4) {
  const board = Array.from({ length: CLUSTER_CELL_COUNT }, () => random(state, colourCount));
  if (!clusterMoves(board)) board[1] = board[0];
  return board;
}

/** Initialize a scored board with finite goals; all randomness uses the replay seed. */
export function createBoardGame(state) {
  switch (state.gameId) {
    case 'gem-swap':
      createGemGame(state);
      break;
    case 'cluster-pop':
      state.data = { board: clusterBoard(state), turns: 0, combo: 0, level: 1 };
      break;
    case 'soft-merge': {
      const board = Array(MERGE_CELL_COUNT).fill(0);
      const first = random(state, MERGE_CELL_COUNT);
      board[first] = 2;
      const empty = board.map((v, i) => (!v ? i : -1)).filter((i) => i >= 0);
      board[empty[random(state, empty.length)]] = 2;
      state.data = { board, turns: 0, milestone: false, combo: 0, level: 1, milestones: [] };
      break;
    }
    case 'block-fit':
      state.data = {
        board: Array(36).fill(0),
        tray: makeTray(state),
        selected: 0,
        turns: 0,
        combo: 0,
        level: 1,
      };
      syncPiece(state.data);
      break;
    default:
      reject('Unknown board game.');
  }
}

/** Preview/award the same bounded big-group combo policy. Small groups break the
 * streak; selection is never a score-bearing action. */
export function clusterReward(data, size) {
  const combo = size >= 4 ? Math.min(5, data.combo + 1) : 0;
  return {
    combo,
    points: 10 * size * (size - 1) * data.level + Math.max(0, combo - 1) * 25 * data.level,
  };
}

/** Describe one visible burst using the canonical award policy. Points are
 * integer score units; chainBonus is additive, never a score multiplier.
 * Invalid indices/singletons return zero points and tier none. Tiers describe
 * group size for presentation only and cannot alter score, RNG or transitions. */
export function clusterBurst(data, index) {
  const cells =
      Number.isInteger(index) && index >= 0 && index < data.board.length
        ? clusterGroup(data.board, index)
        : [],
    size = cells.length,
    { points, combo } = size >= 2 ? clusterReward(data, size) : { points: 0, combo: 0 },
    tier =
      size >= 16
        ? 'super'
        : size >= 10
          ? 'mega'
          : size >= 6
            ? 'big'
            : size >= 4
              ? 'chain'
              : size >= 2
                ? 'pop'
                : 'none';
  return {
    cells,
    size,
    points,
    combo,
    tier,
    chainBonus: Math.max(0, combo - 1) * 25 * data.level,
  };
}

/** Settle survivors only within their original column, preserving bottom-to-top
 * order. Seeded refills enter the gaps above them; animation metadata describes
 * actual accepted movement and never repairs an unavailable group by shuffling. */
function settleClusters(state) {
  const d = state.data,
    movements = [],
    refills = [];
  for (let column = 0; column < CLUSTER_COLUMNS; column++) {
    let destination = CLUSTER_CELL_COUNT - CLUSTER_COLUMNS + column;
    for (let row = CLUSTER_COLUMNS - 1; row >= 0; row--) {
      const source = row * CLUSTER_COLUMNS + column;
      if (d.board[source] === null) continue;
      d.board[destination] = d.board[source];
      if (source !== destination) movements.push([source, destination]);
      destination -= CLUSTER_COLUMNS;
    }
    for (; destination >= 0; destination -= CLUSTER_COLUMNS) {
      const colour = random(state, d.level + 3);
      d.board[destination] = colour;
      refills.push([destination, colour]);
    }
  }
  d.events.push({ type: 'gravity', movements, refills, boardAfter: [...d.board] });
}

function popCluster(state, move) {
  const d = state.data;
  if (move.type !== 'cell') reject('Choose a connected group; bubbles are never shuffled.');
  const group = clusterGroup(d.board, move.index);
  if (group.length < 2) reject('Choose a connected group.');
  const { points, combo } = clusterReward(d, group.length);
  state.score += points;
  d.turns++;
  d.combo = combo;
  d.events = [{ type: 'clear', cells: group, points, combo }];
  group.forEach((i) => (d.board[i] = null));
  settleClusters(state);
  advanceBoardLevel(state, 7, 14);
  state.completed = d.turns >= 20 || !clusterMoves(d.board);
}

function mergeLine(values) {
  const compact = values.filter(Boolean);
  const line = [];
  let points = 0;
  for (let i = 0; i < compact.length; i++) {
    if (compact[i] === compact[i + 1]) {
      line.push(compact[i] * 2);
      points += compact[i] * 2;
      i++;
    } else line.push(compact[i]);
  }
  while (line.length < MERGE_COLUMNS) line.push(0);
  return { line, points };
}

function mergeHasMove(board) {
  return (
    board.some((value) => !value) ||
    board.some((value, index) =>
      neighbors(index, MERGE_COLUMNS, MERGE_CELL_COUNT).some((next) => board[next] === value),
    )
  );
}

function mergeTiles(state, direction) {
  const d = state.data,
    before = [...d.board];
  let points = 0;
  for (let lane = 0; lane < MERGE_COLUMNS; lane++) {
    const indexes = Array.from({ length: MERGE_COLUMNS }, (_, i) =>
      direction < 2
        ? lane * MERGE_COLUMNS + (direction === 0 ? i : MERGE_COLUMNS - 1 - i)
        : (direction === 2 ? i : MERGE_COLUMNS - 1 - i) * MERGE_COLUMNS + lane,
    );
    const result = mergeLine(indexes.map((i) => d.board[i]));
    points += result.points;
    indexes.forEach((index, i) => (d.board[index] = result.line[i]));
  }
  if (d.board.every((v, i) => v === before[i])) {
    state.completed = !mergeHasMove(d.board);
    return;
  }
  d.turns++;
  d.combo = points ? Math.min(5, d.combo + 1) : 0;
  const comboPoints = points ? Math.max(0, d.combo - 1) * 10 : 0;
  state.score += points + comboPoints;
  d.events = [
    {
      type: 'slide',
      direction,
      cells: d.board.map((v, i) => (v !== before[i] ? i : -1)).filter((i) => i >= 0),
      points: points + comboPoints,
      combo: d.combo,
    },
  ];
  const empty = d.board.map((v, i) => (!v ? i : -1)).filter((i) => i >= 0);
  const index = empty[random(state, empty.length)];
  const base = mergeSpawnBase(Math.max(...d.board));
  d.board[index] = base * (random(state, 10) === 0 ? 2 : 1);
  d.events.push({ type: 'spawn', cells: [index] });
  const largest = Math.max(...d.board);
  for (const target of MERGE_MILESTONES) {
    if (largest < target || d.milestones.includes(target)) continue;
    d.milestones.push(target);
    const points = target;
    state.score += points;
    d.events.push({ type: 'milestone', label: `${target} reached`, points, target });
  }
  d.level =
    largest >= 4096
      ? 6
      : largest >= 1024
        ? 5
        : largest >= 256
          ? 4
          : largest >= 128
            ? 3
            : largest >= 64
              ? 2
              : 1;
  d.milestone = largest >= MERGE_TARGET;
  state.completed = d.milestone || !mergeHasMove(d.board);
}

function makeTray(state, level = 1) {
  return Array.from({ length: 3 }, () =>
    structuredClone(
      BLOCK_SHAPES[random(state, level === 1 ? 5 : level === 2 ? 7 : BLOCK_SHAPES.length)],
    ),
  );
}
function syncPiece(d) {
  d.piece = d.tray[d.selected] || [];
}
export function rotateBlock(piece) {
  const result = piece.map(([x, y]) => [-y, x]);
  const minX = Math.min(...result.map((p) => p[0])),
    minY = Math.min(...result.map((p) => p[1]));
  return result.map(([x, y]) => [x - minX, y - minY]);
}

/** Return the exact empty footprint at a top-left anchor, or an empty invalid result. */
export function blockPlacement(data, anchor, piece = data.piece) {
  if (!Number.isInteger(anchor) || anchor < 0 || anchor >= 36) return [];
  const x = anchor % 6,
    y = Math.floor(anchor / 6);
  const indexes = piece.map(([dx, dy]) => (x + dx < 6 && y + dy < 6 ? (y + dy) * 6 + x + dx : -1));
  return indexes.length && indexes.every((i) => i >= 0 && !data.board[i]) ? indexes : [];
}
export function blocksAvailable(d) {
  return d.tray.some((original) => {
    if (!original) return false;
    let piece = original;
    for (let turn = 0; turn < 4; turn++) {
      if (Array.from({ length: 36 }, (_, i) => blockPlacement(d, i, piece).length).some(Boolean))
        return true;
      piece = rotateBlock(piece);
    }
    return false;
  });
}

/** Derive simultaneous full lanes from one 6×6 placement snapshot. Intersections
 * appear once in cells; both lanes count as earned clears. UI prediction reuses
 * this policy, so a landing ghost cannot promise a different accepted clear. */
export function blockClearLines(board) {
  const rows = [],
    columns = [],
    cells = new Set();
  for (let lane = 0; lane < 6; lane++) {
    for (const [axis, indexes] of [
      [rows, Array.from({ length: 6 }, (_, i) => lane * 6 + i)],
      [columns, Array.from({ length: 6 }, (_, i) => i * 6 + lane)],
    ])
      if (indexes.every((i) => board[i])) {
        axis.push(lane);
        indexes.forEach((i) => cells.add(i));
      }
  }
  return { rows, columns, cells: [...cells] };
}

function fitBlock(state, move) {
  const d = state.data;
  if (move.type === 'option') {
    if (move.index === 0) d.tray[d.selected] = rotateBlock(d.piece);
    else {
      if (!d.tray[move.index - 1]) reject('That piece has been used.');
      d.selected = move.index - 1;
    }
    syncPiece(d);
    return;
  }
  const indexes = blockPlacement(d, move.index);
  if (!indexes.length) reject('That piece does not fit.');
  indexes.forEach((i) => (d.board[i] = d.selected + 1));
  d.turns++;
  const { rows, columns, cells } = blockClearLines(d.board);
  const cleared = new Set(cells);
  const lines = rows.length + columns.length;
  d.combo = lines ? d.combo + 1 : 0;
  const placementPoints = indexes.length * 5;
  const clearPoints =
    lines * 60 + (lines > 1 ? (lines - 1) * 60 : 0) + Math.min(d.combo, 5) * 10 * d.level;
  const points = placementPoints + clearPoints;
  state.score += points;
  d.events = [{ type: 'place', cells: indexes, points: placementPoints, boardAfter: [...d.board] }];
  if (lines) {
    const boardBefore = [...d.board];
    cleared.forEach((i) => (d.board[i] = 0));
    d.events.push({
      type: 'clear',
      cells: [...cleared],
      lines,
      rows,
      columns,
      combo: d.combo,
      points: clearPoints,
      boardBefore,
    });
  }
  if (lines && d.board.every((value) => value === 0)) {
    const points = 150 * d.level;
    state.score += points;
    d.events.push({ type: 'all-clear', points, combo: d.combo });
  }
  advanceBoardLevel(state, 10, 20);
  d.tray[d.selected] = null;
  if (d.tray.every((p) => !p)) d.tray = makeTray(state, d.level);
  d.selected = d.tray.findIndex(Boolean);
  syncPiece(d);
  state.completed = d.turns >= 30 || !blocksAvailable(d);
}

/** Mutate only the engine-owned clone; selection/rotation/shuffle never earn points. */
export function moveBoardGame(state, move) {
  state.data.events = [];
  switch (state.gameId) {
    case 'gem-swap':
      gemSwap(state, move.index);
      break;
    case 'cluster-pop':
      popCluster(state, move);
      break;
    case 'soft-merge':
      mergeTiles(state, move.index);
      break;
    case 'block-fit':
      fitBlock(state, move);
      break;
  }
}
